import { configureStore, combineReducers, type Middleware } from '@reduxjs/toolkit'
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import apiClient from '../services/apiClient'
import authReducer, { signOut } from '../slices/auth'
import configReducer from '../slices/config'
import { disconnectFeathersSocket } from '../services/feathersSocket'

/** Vite/ESM breaks `redux-persist/lib/storage` default export — use a local adapter. */
const storage = {
  getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key: string, value: string) => {
    localStorage.setItem(key, value)
    return Promise.resolve(value)
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key)
    return Promise.resolve()
  },
}

const persistConfig = {
  key: 'driveops-web',
  storage,
  whitelist: ['auth', 'config'],
}

const rootReducer = combineReducers({
  auth: authReducer,
  config: configReducer,
  [apiClient.reducerPath]: apiClient.reducer,
})

const persistedReducer = persistReducer(persistConfig, rootReducer)

const signOutMiddleware: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action)
  if (signOut.match(action)) {
    disconnectFeathersSocket()
    storeApi.dispatch(apiClient.util.resetApiState())
  }
  return result
}

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(apiClient.middleware)
      .concat(signOutMiddleware),
})

export const persistor = persistStore(store)
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
